import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionDetail, type PositionNode } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { Modal } from "../../components/Modal";
import { PositionTreePicker } from "../../components/PositionTreePicker";
import { CopyExcludePicker } from "./CopyExcludePicker";
import { useStructureScope } from "../../lib/structureScope";
import { errorBox, positionTitle, translateError } from "./shared";

export function CopyModal({
  position, onClose, onCopied,
}: {
  position: PositionDetail;
  onClose: () => void;
  onCopied: (targetParentId: string | null, copies: PositionDetail[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  // The copy goes only where the user may change the structure (sprint 7 §12); the source may be anywhere.
  const scope = useStructureScope();
  const parentInScope = scope.inScope(position.path.map((p) => p.id));
  const [target, setTarget] = useState<PositionNode | null>(null);
  // Default target = next to the source: its parent, or the first level for a first-level position.
  const [targetId, setTargetId] = useState<string | null>(parentInScope ? position.parentId : null);
  const [toTopLevel, setToTopLevel] = useState(position.parentId === null && !scope.limited);
  const [name, setName] = useState(position.name);
  const [code, setCode] = useState("");
  const [quantity, setQuantity] = useState("1");
  // Positions of the branch left out of the copy (each with everything below it); empty = the whole branch.
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parentId = toTopLevel ? null : targetId;
    if (!toTopLevel && !targetId) {
      setError(t("structure.modal.noTarget", "Nije izabran cilj."));
      return;
    }
    const qty = Number.parseInt(quantity, 10);
    if (!Number.isInteger(qty) || qty < 1 || qty > 20) {
      setError(t("structure.error.copyQuantity", "Količina mora biti ceo broj od 1 do 20."));
      return;
    }
    setSubmitting(true);
    try {
      const copies = await structureApi.copy(position.id, {
        targetParentId: parentId,
        name: name.trim() || null,
        code: code.trim() || null,
        quantity: qty,
        excludeIds: excluded.size > 0 ? [...excluded] : undefined,
      });
      await onCopied(parentId, copies);
    } catch (err) {
      setError(translateError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  const targetLabel = target
    ? positionTitle(target)
    : targetId
      ? position.path[position.path.length - 1]?.name ?? "…"
      : null;

  return (
    <Modal
      title={t("structure.modal.copyTitle", "Kopiranje grane")}
      subtitle={excluded.size === 0
        ? t("structure.modal.copySub", "Kopira „{{name}}“ sa svim pod-pozicijama ({{count}}) kao nove pozicije.", { name: positionTitle(position), count: position.descendantCount })
        : t("structure.modal.copySubPartial", "Kopira „{{name}}“ kao nove pozicije, bez izostavljenih pod-pozicija.", { name: positionTitle(position) })}
      onClose={onClose}
      width={640}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {position.descendantCount > 0 && (
          <div className="copy-scope">
            <div className="copy-scope-head">
              <span className="muted" style={{ fontSize: 12.5 }}>
                {excluded.size === 0
                  ? t("structure.modal.copyWhole", "Kopira se cela grana.")
                  : t("structure.modal.copyExcluded", "Izostavljeno iz kopije: {{count}} (sa svime ispod).", { count: excluded.size })}
              </span>
              <button type="button" className="link-btn" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
                {editing ? t("structure.modal.copyEditHide", "Sakrij izbor") : t("structure.modal.copyEdit", "Izmeni šta se kopira")}
              </button>
              {excluded.size > 0 && (
                <button type="button" className="link-btn" onClick={() => setExcluded(new Set())}>
                  {t("structure.modal.copyReset", "Vrati sve")}
                </button>
              )}
            </div>
            {editing && (
              <>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {t("structure.modal.copyEditHelp", "Klikni na poziciju koju ne želiš u kopiji — izostavlja se zajedno sa svime ispod nje.")}
                </span>
                <CopyExcludePicker rootId={position.id} excluded={excluded} onChange={setExcluded} />
              </>
            )}
          </div>
        )}
        {!scope.limited && (
          <label className="check-row">
            <input type="checkbox" checked={toTopLevel} onChange={(e) => setToTopLevel(e.target.checked)} />
            {t("structure.modal.toTopLevel", "Na prvi nivo (bez roditelja)")}
          </label>
        )}
        {!toTopLevel && (
          <div className="field">
            <span>{t("structure.modal.copyTarget", "Cilj (roditelj kopije)")}</span>
            <PositionTreePicker value={targetId} onChange={(n) => { setTarget(n); setTargetId(n.id); }} excludeId={position.id} scoped />
          </div>
        )}
        <span className="muted" style={{ fontSize: 12.5 }}>
          {toTopLevel
            ? t("structure.modal.copyToTop", "Kopija ide na prvi nivo.")
            : targetLabel
              ? t("structure.modal.copyTargetLabel", "Kopija ide pod: {{name}}", { name: targetLabel })
              : t("structure.modal.noTarget", "Nije izabran cilj.")}
        </span>
        <div className="form-grid">
          <label className="field">
            <span>{t("structure.field.name", "Naziv")}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder={position.name} />
          </label>
          <label className="field">
            <span>{t("structure.field.code", "Šifra")}</span>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={40} autoCapitalize="characters" style={{ fontFamily: "var(--mono)" }} placeholder={t("structure.modal.copyCodePlaceholder", "prazno = bez šifre")} />
          </label>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>{t("structure.field.quantity", "Količina")} <span className="req-mark">*</span></span>
            <input className="input" type="number" min={1} max={20} value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" required />
          </label>
          <div className="field">
            <span>&nbsp;</span>
            <span className="muted" style={{ fontSize: 12.5, alignSelf: "center" }}>
              {t("structure.modal.copyQuantityHint", "Šifre unutar grane se ne kopiraju (bile bi duplikati).")}
            </span>
          </div>
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || (!toTopLevel && !targetId)}>
            {submitting ? t("action.saving", "Čuvanje…") : t("structure.copy", "Kopiraj granu")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
